import { AxiosInstance } from "axios";
import { AccountBase, AccountTag } from "components/ai-chat copy/types";
import { SiteInSiteSummary, SiteSummary } from "types/portfolio";
import { convertJsonToCsv, optimizeJsonData } from "components/ai-chat copy/utils";

export function useDataActions(axios: AxiosInstance) {
  const getAccountData = async (
    account: AccountBase,
    option: string
  ): Promise<{ account: AccountBase; option: string; data: any }> => {
    // Simulate an API call to get account data
    let accountKey = account.accountKey || "";
    switch (option) {
      case "Account Details":
        const res = await axios.post(
          "/api/portfolio/accounts",
          {
            sorts: [
              {
                field: "priorityIndicatorRank",
                sort: "asc",
              },
            ],
            filters: [
              {
                field: "accountId",
                operator: "eq",
                value: account.accountId,
              },
            ],
            searchEnable: false,
            keyword: "",
            viewType: "my",
          },
          {
            headers: {
              "Page-Number": 0,
              "Page-Size": 1,
            },
          }
        );
        return {
          account,
          option,
          data: { accountDetailData: res.data },
        };

      case "Subscription Details":
        const { data } = await axios.get(
          `/api/dataservice/v1/accounts/${account.accountId}/subscriptions?includeSite=true&includeSku=true&includeSiteInactive=true&includeRenewal=true&includeHealthInfo=true&latestSubscriptionOnly=1&sorts=invoiceArr:desc&usingBundle=true`
        );
        const optimizedJsonData = optimizeJsonData(data);
        // console.log("Optimized Data:", optimizedJsonData);
        return {
          account,
          option,
          data: { subscriptionSkuSiteData: optimizedJsonData ?? data },
        };

      case "Success Plan Details":
        const { data: successPlanData } = await axios.get(
          `/api/dataservice/v1/success-plans?accountId=${account.accountId}`
        );
        return {
          account,
          option,
          data: {
            successPlanData: {
              createdDate: successPlanData.createdDate,
              createdBy: successPlanData.createdBy,
              lastModifiedDate: successPlanData.lastModifiedDate,
              lastModifiedBy: successPlanData.lastModifiedBy,
              customerRisk: successPlanData.customerRisk,
              customerRiskNote: successPlanData.customerRiskNote,
              meetingRisk: successPlanData.meetingRisk,
              meetingRiskNote: successPlanData.meetingRiskNote,
              callingRisk: successPlanData.callingRisk,
              callingRiskNote: successPlanData.callingRiskNote,
              contactCenterRisk: successPlanData.contactCenterRisk,
              contactCenterRiskNote: successPlanData.contactCenterRiskNote,
              deviceRisk: successPlanData.deviceRisk,
              deviceRiskNote: successPlanData.deviceRiskNote,
              ciscoExeSponsor: successPlanData.ciscoExeSponsor,
              collabExeSponsor: successPlanData.collabExeSponsor,
            },
          },
        };

      case "Recent Activities":
        const { data: activitiesData } = await axios.post(
          "/api/dataservice/v1/activities/smart-search",
          {
            includeTasks: 1,
            includePlaycards: 1,
            includeComments: 1,
            filters: [
              {
                field: "accountId",
                operator: "eq",
                value: account.accountId,
              },
            ],
            sorts: [
              {
                field: "createdDate",
                order: "desc",
              },
            ],
            viewType: "all",
          },
          {
            headers: {
              "Page-Number": 0,
              "Page-Size": 3,
            },
          }
        );

        const optimizedRawData = activitiesData.map((item: any) => ({
          type: item.type,
          activityName: item.activityName,
          activityStatus: item.activityStatus,
          createdDate: item.createdDate,
          assigneeName: item.assigneeName,
          lastModifiedDate: item.lastModifiedDate,
          overdue: item.overdue,
          closedDate: item.closedDate,
          allTasks: item.allTasks,
          closedTasks: item.closedTasks,
          overdueTasks: item.overdueTasks,
          products: item.products,
          purpose: item.purpose,
          description: item.description,
          tasks:
            Array.isArray(item.tasks) && item.tasks.length > 0
              ? item.tasks.map((task: any) => ({
                  taskName: task.taskName,
                  taskStatus: task.taskStatus,
                  taskDueDate: task.dueDate,
                  taskClosedDate: task.closedDate,
                  taskDescription: task.taskDescription,
                  taskAssigneeRole: task.taskAssigneeRole,
                  taskAssigneeName: task.taskAssigneeName,
                }))
              : [],
          comments:
            Array.isArray(item.comments) && item.comments.length > 0
              ? item.comments.map((comment: any) => ({
                  comment: comment.text,
                  createdBy: comment.createdBy,
                  createdDate: comment.createdDate,
                  creatorName: comment.creatorName,
                }))
              : [],
        }));

        return {
          account,
          option,
          data: {
            activitiesData: optimizeJsonData(optimizedRawData) ?? optimizedRawData,
          },
        };

      case "Webex Meeting Usage":
        const { data: siteSummaryArr } = await axios.get<SiteSummary[]>(
          `/api/dataservice/v1/accounts/${account.accountId}/site-summary`
        );

        // If siteSummaryArr is an array, get the first element or handle accordingly
        const siteSummary = Array.isArray(siteSummaryArr) ? siteSummaryArr[0] : siteSummaryArr;

        if (!siteSummary || !siteSummary.sites || siteSummary.sites.length === 0) {
          return {
            account,
            option,
            data: {
              webexMeetingUsage: "No webex meeting usage",
              webexMeetingUtilization: "No webex meeting utilization",
            },
          };
        }

        const { data: CHAligedUsageData } = await axios.get(
          `/api/summary/usage/meeting?hgsSiteId=${siteSummary?.sites
            ?.filter((x: SiteInSiteSummary) => x.hasUsage)
            .map((x: SiteInSiteSummary) => x.hgsSiteId)
            .join(",")}&rangeType=monthly&rangeValue=13`
        );

        const { data: utilizationData } = await axios.get(
          `/api/summary/usage/meeting/legacy/utilization?siteNames=${siteSummary?.sites
            ?.filter((x: SiteInSiteSummary) => x.hasUsage)
            .map((x: SiteInSiteSummary) => x.siteUrl.slice(0, -10))
            .join(",")}&rangeValue=12&deploymentModel=${
            siteSummary?.sites?.filter((x: SiteInSiteSummary) => x.hasUsage)[0].deploymentModel
          }`
        );

        return {
          account,
          option,
          data: {
            webexMeetingUsage: {
              categories: CHAligedUsageData.categories,
              data: {
                activeHosts: CHAligedUsageData.data.activeHosts,
                totalMeetings: CHAligedUsageData.data.totalMeetings,
                totalVideoMeetingsVideoMeetings:
                  CHAligedUsageData.data.totalVideoMeetingsVideoMeetings,
                totalMeetingsMinutesMeetingMinutes:
                  CHAligedUsageData.data.totalMeetingsMinutesMeetingMinutes,
                totalPeopleMinutes: CHAligedUsageData.data.totalPeopleMinutes,
                sharingMeetings: CHAligedUsageData.data.sharingMeetings,
              },
            },
            webexMeetingUtilization: utilizationData,
          },
        };

      case "Webex Calling Usage":
        if (!accountKey || accountKey === "") {
          const { data: accountData } = await axios.get(
            `/api/dataservice/v1/accounts/${account.accountId}?includeSite=false&usingBundle=false&includeSubscriptions=false`
          );
          accountKey = accountData.accountKey;
        }

        const { data: ciOrgsData } = await axios.get(
          `/api/dataservice/v1/accounts/${accountKey}/ci-organizations`
        );
        if (!ciOrgsData || ciOrgsData.length === 0) {
          return {
            account,
            option,
            data: { callingUsageData: "No calling usage data" },
          };
        }

        const { data: callingUsageData } = await axios.post(
          `/api/dataservice/v1/ci-organizations/webex-calling-usage`,
          {
            rangeType: "monthly",
            dateRange: 13,
            orgIds: ciOrgsData.map((org: any) => org.ciOrgId),
          }
        );
        return {
          account,
          option,
          data: { callingUsageData },
        };

      case "Webex Messaging Usage":
        const { data: messagingActiveUserData } = await axios.get(
          `/api/summary/usage/teams/active-users?accountId=${account.accountId}&rangeType=monthly&dateRange=12`
        );
        const { data: messagingUsageData } = await axios.get(
          `/api/summary/usage/teams/teams-usage?accountId=${account.accountId}&dateRange=12`
        );
        return {
          account,
          option,
          data: { messagingActiveUserData, messagingUsageData },
        };

      // Simulated data for other options
      case "Technical Support Cases":
        const { data: TechnicalSupportCasesData } = await axios.get(
          `/api/dataservice/v1/accounts/${account.accountId}/case-summary?months=12`
        );
        if (!accountKey || accountKey === "") {
          const { data: accountData } = await axios.get(
            `/api/dataservice/v1/accounts/${account.accountId}?includeSite=false&usingBundle=false&includeSubscriptions=false`
          );
          accountKey = accountData.accountKey;
        }
        const { data: TechnicalSupportCasesDetailData } = await axios.get(
          `/api/dataservice/v1/accounts/${accountKey}/cases-list?caseType=TAC,BEMS,CAP&status=Open&sorts=severity:asc,date:desc`
        );
        return {
          account,
          option,
          data: {
            TechnicalSupportCasesData,
            TechnicalSupportCasesDetailData: convertJsonToCsv(
              TechnicalSupportCasesDetailData,
              true
            ),
          },
        };
      case "My Notes":
        const { data: myNotesData } = await axios.get(`/api/dataservice/v1/my-notes`, {
          params: {
            accountId: account.accountId,
            pageSize: 100,
            pageNumber: 0,
            archiveFolder: false,
            sortBy: "createdDate:desc",
          },
        });
        return {
          account,
          option,
          data: { myNotesData: convertJsonToCsv(myNotesData, true) },
        };

      default:
        return {
          account,
          option,
          data: `No data available for option: ${option}`,
        };
    }
  };

  const loadAccountData = async (accountTag: AccountTag): Promise<AccountTag> => {
    if (!accountTag || accountTag.options.length === 0) {
      return accountTag;
    }

    // Collect all promises for parallel loading
    const loadPromises: Promise<{
      account: AccountBase;
      option: string;
      data: any;
    }>[] = [];

    accountTag.options.forEach((opt) => {
      if (!opt.loaded) {
        loadPromises.push(getAccountData(accountTag.account, opt.option));
      }
    });

    // Load all data in parallel
    const results = await Promise.allSettled(loadPromises);

    // Build updated tags with loaded data
    const updatedTag: AccountTag = {
      ...accountTag,
      options: accountTag.options.map((opt) => {
        const result = results.find(
          (res) =>
            res.status === "fulfilled" &&
            res.value.account.accountId === accountTag.account.accountId &&
            res.value.option === opt.option
        ) as
          | PromiseFulfilledResult<{
              account: AccountBase;
              option: string;
              data: any;
            }>
          | undefined;

        if (result) {
          return {
            ...opt,
            data: result.value.data,
            loaded: true,
          };
        }
        return opt;
      }),
    };

    return updatedTag;
  };

  return { loadAccountData };
}
